import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Loader2 } from "lucide-react";

const SIDE_IMG = "https://images.unsplash.com/photo-1636144896336-b056be4a8dfe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxlbGVnYW50JTIwbWF0dXJlJTIwYmxhY2slMjB3b21hbiUyMHBvcnRyYWl0fGVufDB8fHx8MTc4ODYyODc4Mnww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/dashboard");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-sand text-ink">
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-16">
        <Link to="/" className="font-serif text-2xl mb-16" data-testid="brand-logo">Élanora</Link>
        <div className="max-w-md">
          <p className="eyebrow mb-4">Welcome back</p>
          <h1 className="text-4xl sm:text-5xl tracking-tight">Sign in to your studio</h1>
          <form onSubmit={submit} className="mt-12 space-y-8">
            <div>
              <label className="block text-sm text-ink/60 mb-2">Email</label>
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="w-full bg-transparent border-b border-ink/40 focus:border-emerald outline-none py-3 text-lg"
              />
            </div>
            <div>
              <label className="block text-sm text-ink/60 mb-2">Password</label>
              <input
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="w-full bg-transparent border-b border-ink/40 focus:border-emerald outline-none py-3 text-lg"
              />
            </div>
            {error && <p className="text-burgundy text-sm" data-testid="login-error">{error}</p>}
            <button
              type="submit" disabled={loading} data-testid="login-submit"
              className="w-full rounded-full bg-emerald text-white py-4 text-base hover:bg-emerald/90 transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />}
              Sign in
            </button>
          </form>
          <p className="mt-8 text-ink/60">
            New here?{" "}
            <Link to="/register" className="text-emerald underline underline-offset-4" data-testid="link-register">
              Create your profile
            </Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:block relative">
        <img src={SIDE_IMG} alt="Elegant styling portrait" className="absolute inset-0 w-full h-full object-cover" />
      </div>
    </div>
  );
}
